import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import restaurantService from '../../services/restaurant.service';
import realtimeService from '../../services/realtime.service';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Paper,
  Chip,
  Avatar,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigationIcon from '@mui/icons-material/Navigation';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PercentIcon from '@mui/icons-material/Percent';
import StripeConnectSection from '../../components/StripeConnectSection';

const OFFER_TIMEOUT = 60;

// -- Compte a rebours circulaire --
const CountdownTimer = ({ seconds, total }) => {
  const pct = (seconds / total) * 100;
  const color = seconds > 20 ? '#4caf50' : seconds > 10 ? '#ff9800' : '#f44336';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={pct}
        size={80}
        thickness={5}
        sx={{ color }}
      />
      <Box sx={{
        top: 0, left: 0, bottom: 0, right: 0,
        position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {seconds}
        </Typography>
      </Box>
    </Box>
  );
};

const DeliveryPartner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Duty status
  const [isOnDuty, setIsOnDuty] = useState(false);
  const [dutyLoading, setDutyLoading] = useState(false);
  const [gpsActive, setGpsActive] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  // Active order
  const [activeOrder, setActiveOrder] = useState(null);

  // Delivery offer
  const [offer, setOffer] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [offerLoading, setOfferLoading] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  const watchIdRef = useRef(null);
  const countdownRef = useRef(null);
  const locationPushRef = useRef(false);
  const locationPushTimerRef = useRef(null);

  // -- Load data --
  const loadData = useCallback(async () => {
    try {
      const [statsRes, activeRes, pendingRes] = await Promise.allSettled([
        restaurantService.getDriverStats(),
        restaurantService.getDriverActiveOrder(),
        restaurantService.getDriverPendingOffer(),
      ]);
      if (statsRes.status === 'fulfilled' && statsRes.value.success) {
        setStats(statsRes.value.stats);
        setIsOnDuty(!!statsRes.value.stats.is_on_duty);
      }
      if (activeRes.status === 'fulfilled' && activeRes.value.success) {
        setActiveOrder(activeRes.value.order);
      }
      if (pendingRes.status === 'fulfilled' && pendingRes.value.success && pendingRes.value.offer) {
        setOffer(pendingRes.value.offer);
        setCountdown(pendingRes.value.offer.expiresIn || OFFER_TIMEOUT);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // -- Realtime subscription --
  useEffect(() => {
    realtimeService.connect();

    const handleOffer = (data) => {
      setOffer(data);
      setCountdown(data.expiresIn || OFFER_TIMEOUT);
    };

    const handleOfferCancelled = () => {
      clearInterval(countdownRef.current);
      setOffer(null);
    };

    realtimeService.onDeliveryOffer(handleOffer);
    realtimeService.onDeliveryOfferCancelled(handleOfferCancelled);

    loadData();

    return () => {
      realtimeService.off('delivery-offer');
      realtimeService.off('delivery-offer-cancelled');
    };
  }, [loadData]);

  // -- Countdown timer --
  useEffect(() => {
    if (!offer) return;
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setOffer(null);
          setSnack({ open: true, msg: "Offre expiree", sev: 'warning' });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [offer]);

  // -- GPS tracking --
  const startGps = useCallback(() => {
    if (!navigator.geolocation) return;
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsActive(true);
        if (!locationPushRef.current) {
          locationPushRef.current = true;
          restaurantService.updateDriverLocation(
            pos.coords.latitude,
            pos.coords.longitude
          ).catch(() => {});
          if (locationPushTimerRef.current) clearTimeout(locationPushTimerRef.current);
          locationPushTimerRef.current = setTimeout(() => { locationPushRef.current = false; }, 15000);
        }
      },
      () => setGpsActive(false),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  }, []);

  const stopGps = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setGpsActive(false);
  }, []);

  useEffect(() => {
    if (isOnDuty) {
      startGps();
    } else {
      stopGps();
    }
  }, [isOnDuty, startGps, stopGps]);

  useEffect(() => {
    return () => {
      stopGps();
      if (locationPushTimerRef.current) clearTimeout(locationPushTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [stopGps]);

  // -- Toggle duty --
  const handleToggleDuty = async () => {
    setDutyLoading(true);
    const next = !isOnDuty;
    try {
      const res = await restaurantService.toggleDriverDuty(next);
      if (res.success) {
        setIsOnDuty(res.is_on_duty);
        setSnack({
          open: true,
          msg: res.is_on_duty ? "Vous etes en service" : "Vous etes hors service",
          sev: res.is_on_duty ? 'success' : 'info'
        });
      }
    } catch {
      setSnack({ open: true, msg: "Erreur reseau", sev: 'error' });
    } finally {
      setDutyLoading(false);
    }
  };

  // -- Accept offer --
  const handleAccept = async () => {
    if (!offer) return;
    setOfferLoading(true);
    clearInterval(countdownRef.current);
    try {
      const res = await restaurantService.acceptDelivery(offer.orderId);
      if (res.success) {
        setOffer(null);
        setSnack({ open: true, msg: "Course acceptee ! En route vers le restaurant.", sev: 'success' });
        const activeRes = await restaurantService.getDriverActiveOrder();
        if (activeRes.success) setActiveOrder(activeRes.order);
        navigate(`/delivery/order/${offer.orderId}`);
      } else {
        setSnack({ open: true, msg: res.message || "Course non disponible", sev: 'error' });
        setOffer(null);
      }
    } catch {
      setSnack({ open: true, msg: "Erreur lors de l'acceptation", sev: 'error' });
      setOffer(null);
    } finally {
      setOfferLoading(false);
    }
  };

  // -- Refuse offer --
  const handleRefuse = async () => {
    if (!offer) return;
    clearInterval(countdownRef.current);
    try {
      await restaurantService.refuseDelivery(offer.orderId);
    } catch {}
    setOffer(null);
    setSnack({ open: true, msg: "Course refusee", sev: 'info' });
  };

  // -- Update delivery status --
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const res = await restaurantService.updateOrderStatus(orderId, newStatus);
      if (res.success) {
        if (newStatus === 'delivered') {
          setActiveOrder(null);
          setSnack({ open: true, msg: "Livraison confirmee !", sev: 'success' });
          loadData(); // Refresh stats
        } else {
          // Refresh active order
          const activeRes = await restaurantService.getDriverActiveOrder();
          if (activeRes.success) setActiveOrder(activeRes.order);
          const statusMessages = {
            picked_up: 'Colis recupere',
            delivering: 'En route vers le client',
          };
          setSnack({ open: true, msg: statusMessages[newStatus] || 'Statut mis a jour', sev: 'success' });
        }
      }
    } catch {
      setSnack({ open: true, msg: "Erreur lors de la mise a jour", sev: 'error' });
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #552583 0%, #33154f 100%)',
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  color: 'white',
                }}
              >
                <DeliveryDiningIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {user?.first_name ? `Bonjour, ${user.first_name}` : 'Espace Livreur'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Partenaire livreur Usafiri
                </Typography>
                <Box display="flex" gap={1} mt={0.5}>
                  <Chip
                    label={isOnDuty ? 'En ligne' : 'Hors ligne'}
                    size="small"
                    sx={{
                      bgcolor: isOnDuty ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255,255,255,0.15)',
                      color: isOnDuty ? '#69f0ae' : 'rgba(255,255,255,0.8)',
                      border: `1px solid ${isOnDuty ? 'rgba(76, 175, 80, 0.6)' : 'rgba(255,255,255,0.4)'}`,
                      fontWeight: 600,
                    }}
                  />
                  {isOnDuty && (
                    <Chip
                      icon={<MyLocationIcon sx={{ fontSize: 14, color: gpsActive ? '#4caf50 !important' : '#f44336 !important' }} />}
                      label={gpsActive ? 'GPS' : 'GPS off'}
                      size="small"
                      sx={{
                        bgcolor: gpsActive ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)',
                        color: gpsActive ? '#4caf50' : '#f44336',
                        border: `1px solid ${gpsActive ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)'}`,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {stats?.rating && (
                    <Chip
                      icon={<StarIcon sx={{ color: '#ffc107 !important', fontSize: 16 }} />}
                      label={stats.rating}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 193, 7, 0.15)',
                        color: '#ffc107',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isOnDuty}
                  onChange={handleToggleDuty}
                  disabled={dutyLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: 'rgba(76, 175, 80, 0.5)',
                    },
                    '& .MuiSwitch-track': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1" fontWeight={700} sx={{ color: 'white' }}>
                  {dutyLoading ? 'Chargement...' : isOnDuty ? 'En service' : 'Hors service'}
                </Typography>
              }
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -3 }}>
        {/* Statistiques rapides */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'rgba(240, 235, 224, 0.15)', color: '#F0EBE0', width: 50, height: 50 }}>
                    <DeliveryDiningIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats?.today_deliveries || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Livraisons aujourd'hui
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'rgba(240, 235, 224, 0.15)', color: '#F0EBE0', width: 50, height: 50 }}>
                    <AttachMoneyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats?.today_earnings?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Gains du jour (EUR)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255, 193, 7, 0.15)', color: '#ffc107', width: 50, height: 50 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats?.total_deliveries || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total livraisons
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.15)', color: '#3498db', width: 50, height: 50 }}>
                    <LocalShippingIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {activeOrder ? 1 : 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Livraison en cours
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Commission banner */}
        <Card
          sx={{
            mb: 3,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <CardContent sx={{ py: '10px !important' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <PercentIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
              <Typography variant="body2" color="text.secondary">
                Commission Usafiri : <strong style={{ color: '#F0EBE0' }}>5 %</strong> prelevee sur chaque course — vos gains affiches sont deja nets.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Alerte si hors ligne */}
        {!isOnDuty && (
          <Alert
            severity="warning"
            icon={<NotificationsActiveIcon />}
            sx={{
              mb: 3,
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
            }}
          >
            Vous etes hors ligne. Activez le mode <strong>"En service"</strong> pour recevoir des commandes de livraison.
          </Alert>
        )}

        {/* Active order banner */}
        {activeOrder && (
          <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main', bgcolor: 'rgba(85,37,131,0.05)' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DeliveryDiningIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>Livraison en cours</Typography>
                </Box>
                <Chip
                  label={`+${(parseFloat(activeOrder.delivery_fee || 0) * 0.95).toFixed(2)} EUR`}
                  color="success"
                  sx={{ fontWeight: 700 }}
                />
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                #{activeOrder.order_number} — {activeOrder.restaurant_name}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              {activeOrder.delivery_address && (
                <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <LocationOnIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                  <Typography variant="body2">{activeOrder.delivery_address}</Typography>
                </Box>
              )}

              {/* Status progression buttons */}
              <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                {activeOrder.order_status === 'ready' && (
                  <Button
                    fullWidth variant="contained" color="info"
                    onClick={() => handleUpdateStatus(activeOrder.id, 'picked_up')}
                  >
                    Colis recupere
                  </Button>
                )}
                {activeOrder.order_status === 'picked_up' && (
                  <Button
                    fullWidth variant="contained"
                    onClick={() => handleUpdateStatus(activeOrder.id, 'delivering')}
                    sx={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}
                  >
                    En route vers le client
                  </Button>
                )}
                {activeOrder.order_status === 'delivering' && (
                  <Button
                    fullWidth variant="contained"
                    onClick={() => handleUpdateStatus(activeOrder.id, 'delivered')}
                    sx={{ background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)' }}
                  >
                    Confirmer la livraison
                  </Button>
                )}
                <Button
                  fullWidth variant="outlined"
                  startIcon={<NavigationIcon />}
                  onClick={() => navigate(`/delivery/order/${activeOrder.id}`)}
                >
                  Voir details
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dashboard" />
            <Tab label="Paiements" />
          </Tabs>
        </Paper>

        {/* Tab 0: Dashboard */}
        {tabValue === 0 && (
          <Box>
            {isOnDuty && !activeOrder && !offer ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <DeliveryDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Aucune commande disponible pour le moment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Les nouvelles commandes apparaitront automatiquement
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={2}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: '#4caf50',
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%,100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.6)' },
                        '50%': { boxShadow: '0 0 0 6px rgba(76,175,80,0)' },
                      },
                    }} />
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      En attente de commandes...
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : !isOnDuty && !activeOrder ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <DeliveryDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Passez en ligne pour voir les commandes
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleToggleDuty}
                    disabled={dutyLoading}
                    sx={{ mt: 2, background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)' }}
                  >
                    Passer en service
                  </Button>
                </CardContent>
              </Card>
            ) : null}
          </Box>
        )}

        {/* Tab 1: Paiements / Stripe Connect */}
        {tabValue === 1 && (
          <StripeConnectSection commissionRate={5} commissionLabel="par course" />
        )}

        {/* Actions rapides */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/partner/dashboard')}
            >
              Tableau de bord financier
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/delivery/history')}
            >
              Historique complet
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/profile')}
            >
              Mon profil
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* -- Delivery Offer Dialog -- */}
      <Dialog
        open={!!offer}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '2px solid rgba(240, 235, 224, 0.5)',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
          color: 'white',
          p: 3,
          textAlign: 'center',
        }}>
          <NotificationsActiveIcon sx={{ fontSize: 40, mb: 1, color: '#F0EBE0' }} />
          <Typography variant="h5" fontWeight={700}>
            Nouvelle course disponible !
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {offer && (
            <Box>
              <Box display="flex" justifyContent="center" mb={3}>
                <CountdownTimer seconds={countdown} total={OFFER_TIMEOUT} />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Restaurant</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {offer.restaurantName}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">RETRAIT</Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOnIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                  <Typography variant="body2" fontWeight={500}>
                    {offer.restaurantAddress}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">LIVRAISON</Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOnIcon fontSize="small" color="error" sx={{ mt: 0.3 }} />
                  <Typography variant="body2" fontWeight={500}>
                    {offer.deliveryAddress}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                {offer.deliveryFee && (
                  <Chip
                    icon={<AttachMoneyIcon />}
                    label={`${(parseFloat(offer.deliveryFee) * 0.95).toFixed(2)} EUR`}
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      color: '#4caf50',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      py: 2,
                    }}
                  />
                )}
                {offer.distanceKm && (
                  <Chip
                    icon={<LocalShippingIcon />}
                    label={`${offer.distanceKm} km`}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={handleRefuse}
            disabled={offerLoading}
            startIcon={<CloseIcon />}
          >
            Refuser
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleAccept}
            disabled={offerLoading}
            startIcon={offerLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            sx={{ background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)' }}
          >
            {offerLoading ? 'Acceptation...' : 'Accepter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryPartner;
